import {SiteProviderModel} from "../../site-preferences/angular/models/site-provider.model";
import {SiteProviderKind} from "../../site-preferences/angular/types/site-provider-kind.type";
import {isPredictionProviderType} from "../../site-preferences/angular/constants/site-provider-types.constant";
import {SchedulingProjectTemplateModel} from "./scheduling-project-template.model";
import {
  TemplateProviderOptionsModel,
  TemplateProviderOptionsWrapperModel,
  TemplateProviderSelectionModel
} from "./scheduling-project-template-providers.model";

function wrap(list: SiteProviderModel[]): TemplateProviderOptionsWrapperModel {
  return {list: list, propertyRef: 'name'};
}

/** Every data source a saved template points at, whichever field carries it. */
function referencedProviderIds(template?: SchedulingProjectTemplateModel): string[] {
  return [...(template?.providerIds || []), template?.serviceTimeVisitCallPredictionProviderId]
    .filter((providerId) => !!providerId);
}

/**
 * An archived data source is kept out of the pickers, with one exception: a template that already
 * points at it. Dropping it would leave the select with a value it cannot label - and submitting
 * would quietly drop a provider the template still runs on - so a referenced source stays listed
 * until the user replaces it themselves.
 */
function isSelectable(provider: SiteProviderModel, referencedIds: string[]): boolean {
  return !provider.archive || referencedIds.indexOf(provider.id) !== -1;
}

export function buildTemplateProviderOptions(
  providers: SiteProviderModel[],
  template?: SchedulingProjectTemplateModel
): TemplateProviderOptionsModel {
  const referencedIds = referencedProviderIds(template);
  const selectable = (providers || []).filter((provider) => isSelectable(provider, referencedIds));
  const ofKind = (kind: SiteProviderKind) => selectable.filter((provider) => provider.providerKind === kind);
  const serviceTime = ofKind(SiteProviderKind.ServiceTime);

  return {
    serviceTime: wrap(serviceTime),
    units: wrap(ofKind(SiteProviderKind.Units)),
    deliveryTime: wrap(ofKind(SiteProviderKind.DeliveryTime)),
    // The visit call prediction is a live model call, so only a prediction source - the one type
    // that carries an endpointId - can answer it.
    serviceTimeVisitCallPrediction: wrap(serviceTime.filter((provider) => isPredictionProviderType(provider.providerType)))
  };
}

function selectedFrom(wrapper: TemplateProviderOptionsWrapperModel, providerIds: string[]): string {
  const provider = wrapper.list.find((candidate) => providerIds.indexOf(candidate.id) !== -1);

  return provider?.id;
}

/**
 * Restores what a saved template had picked, one select at a time.
 *
 * `providerIds` is a flat array that says nothing about kinds, so which select an id belongs in is
 * decided by the list it turns up in. Matching against the rendered lists also keeps an id the
 * user could no longer see - a data source that was deleted, or retyped since it was picked - from
 * being fed to a select that has no name to show for it.
 */
export function resolveTemplateProviderSelection(
  options: TemplateProviderOptionsModel,
  template?: SchedulingProjectTemplateModel
): TemplateProviderSelectionModel {
  const providerIds = template?.providerIds || [];
  const predictionId = template?.serviceTimeVisitCallPredictionProviderId;

  return {
    serviceTimeId: selectedFrom(options.serviceTime, providerIds),
    unitsId: selectedFrom(options.units, providerIds),
    deliveryTimeId: selectedFrom(options.deliveryTime, providerIds),
    serviceTimeVisitCallPredictionId: predictionId
      ? selectedFrom(options.serviceTimeVisitCallPrediction, [predictionId])
      : undefined
  };
}

/**
 * Whether any of the four selects currently points at an archived data source. It stays selectable
 * so the value renders and nothing is dropped silently, but the server keeps pulling an archived
 * source, so the user is the one who has to move the template onto an active one.
 */
export function hasArchivedProviderSelected(
  options: TemplateProviderOptionsModel,
  selection: TemplateProviderSelectionModel
): boolean {
  if (!options || !selection) {
    return false;
  }

  const isArchived = (wrapper: TemplateProviderOptionsWrapperModel, providerId: string) =>
    !!providerId && wrapper.list.some((provider) => provider.id === providerId && provider.archive);

  return isArchived(options.serviceTime, selection.serviceTimeId) ||
    isArchived(options.units, selection.unitsId) ||
    isArchived(options.deliveryTime, selection.deliveryTimeId) ||
    isArchived(options.serviceTimeVisitCallPrediction, selection.serviceTimeVisitCallPredictionId);
}

/**
 * The `providerIds` the template is saved with. The visit call prediction source is deliberately
 * absent: it is not a provider the template runs on, and travels in its own field.
 */
export function buildTemplateProviderIds(selection: TemplateProviderSelectionModel): string[] {
  return [selection?.serviceTimeId, selection?.unitsId, selection?.deliveryTimeId]
    .filter((providerId) => !!providerId);
}
